import {Component, effect, inject, signal, untracked} from '@angular/core';
import {Location, NgClass} from "@angular/common";
import {DataSubscriptionService} from "src/app/infrastructure/data-subscription.service";
import {GenericButtonComponent} from "src/app/presentation/common/generic-button/generic-button.component";
import {DataSynchronisationService} from "src/app/infrastructure/data-synchronisation.service";
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {ActionType, DataType} from "src/app/domain/domain-models";
import {
  Asset,
  Device,
  DeviceAssociation,
  FixedSamplePoint,
  SynchronizationStatus
} from "src/app/infrastructure/moovement-api/models/asset";
import {FormsModule} from "@angular/forms";
import {FarmStore} from 'src/app/domain/assets/farm/farm.store';
import {MainViewStore} from 'src/app/presentation/main-view/main-view.store';
import {
  DeviceIdInputComponent,
  DeviceIdSelected
} from "src/app/presentation/common/device-id-input/device-id-input.component";
import {NavBarStateStore} from 'src/app/presentation/main-view/nav-bar/nav-bar-state/nav-bar-state.store';
import {Subject, takeUntil} from 'rxjs';
import {
  AddProximitySamplePointStore,
  OnboardingAction
} from 'src/app/presentation/main-view/add-device/add-proximity-sample-point/add-proximity-sample-point.store';
import {BootstrapService} from 'src/app/infrastructure/init/bootstrap.service';
import {
  dropAPinMessage,
  InternalNotification,
  InternalNotificationsStore,
  InternalNotificationType
} from 'src/app/infrastructure/internal-notifications/internal-notifications.store';
import {randomUuid} from 'src/app/domain/util/uuid/uuid';
import {ActivatedRoute, Router} from '@angular/router';
import {FixedSamplePointsStore} from 'src/app/domain/assets/fixed-sample-points/fixed-sample-points.store';
import {DeviceStore} from 'src/app/domain/devices/device.store';
import {DeviceAssociationStore} from 'src/app/domain/devices/device-association.store';
import {BluetoothSvgComponent, Status} from "src/app/presentation/common/icons/bluetooth-svg/bluetooth-svg.component";
import {
  PrecipitationSvgComponent
} from "src/app/presentation/common/icons/precipitation-svg/precipitation-svg.component";
import {SwitchComponent} from "src/app/presentation/common/switch/switch.component";
import {parseOptionalFloat} from 'src/app/domain/util/parsing/parsing';

export enum Destination {
  PROXIMITY_SAMPLE_POINTS = "PROXIMITY_SAMPLE_POINTS"
}

@Component({
  selector: 'app-add-proximity-sample-point',
  imports: [
    GenericButtonComponent,
    NgClass,
    FormsModule,
    DeviceIdInputComponent,
    BluetoothSvgComponent,
    PrecipitationSvgComponent,
    SwitchComponent
],
  templateUrl: './add-proximity-sample-point.component.html',
  styleUrl: './add-proximity-sample-point.component.css'
})
export class AddProximitySamplePointComponent {

  deviceId = signal<string | undefined>(undefined);

  existingDeviceAssociation = signal<DeviceAssociation | undefined>(undefined);
  existingAsset = signal<Asset | undefined>(undefined);

  loading = signal(false);
  validId = signal(false);

  canProcessNewAsset = signal(false);

  OnboardingAction = OnboardingAction;

  ProximitySamplePointStatus = Status;

  private destination?: string;

  private $destroyDropAPin: Subject<void> = new Subject<void>();
  private dropAPinNotification?: InternalNotification;

  store = inject(AddProximitySamplePointStore);
  fixedSamplePointsStore = inject(FixedSamplePointsStore);

  private activatedRoute = inject(ActivatedRoute);
  private deviceAssociationStore = inject(DeviceAssociationStore);
  private deviceStore = inject(DeviceStore);
  private farmStore = inject(FarmStore);
  private internalNotificationsStore = inject(InternalNotificationsStore);
  private location = inject(Location);
  private mainViewStore = inject(MainViewStore);
  private menuStore = inject(NavBarStateStore);
  private router = inject(Router);

  constructor(
    private bootstrapService: BootstrapService,
    private dataSubscriptionService: DataSubscriptionService,
    private dataSynchronisationService: DataSynchronisationService,
    protected viewportService: ViewportService
  ) {
    this.activatedRoute.queryParamMap.subscribe(map => {
      const deviceId = map.get('deviceId');
      if (deviceId) {
        this.store.setId(deviceId);
        this.store.setHardwareId(false);
        this.store.setOnboardingAction(undefined);
      }

      const destination = map.get('destination');
      this.destination = this.toDestination(destination);
    });

    effect(() => {
        const id = this.store.id();
        if (!id || this.deviceStore.loading()) return;

        untracked(() => this.processDeviceId(id, this.store.hardwareId()));
    });

    effect(() => {
      this.canProcessNewAsset.set(
        !this.loading() &&
        this.farmStore.farm() !== undefined &&
        this.store.name() !== undefined &&
        this.store.lat() !== undefined && this.store.long() !== undefined
      );
    });
  }

  private toDestination(destination: string | null) {
    if (destination == Destination.PROXIMITY_SAMPLE_POINTS) return Destination.PROXIMITY_SAMPLE_POINTS;

    return undefined;
  }

  back(): void {
    this.location.back();
  }

  close() {
    this.mainViewStore.hideChildComponent();
  }

  onDeviceIdSelected(deviceIdSelected: DeviceIdSelected) {
    const id = deviceIdSelected.id;
    if (!id) return;

    this.store.setId(id);
    this.store.setHardwareId(!deviceIdSelected.manualInput);
  }

  processDeviceId(id: string, hardwareId: boolean) {
    const devices = this.deviceStore.entities();

    let device: Device | undefined;
    if (hardwareId) {
      device = devices.find(device => device.hardwareId?.toLowerCase() === id.toLowerCase());
    } else {
      device = devices.find(device => device.id?.toLowerCase()?.endsWith(id.toLowerCase()));
    }
    if (!device) {
      this.internalNotificationsStore.addNotification(
        `Scanner '${id}' is not assigned to your farm.`,
        InternalNotificationType.ERROR,
        false
      );
      this.store.setId(undefined);
      return;
    }

    this.deviceId.set(device.id.slice(-5));

    this.store.setDeviceToMatch(device);
    this.processMatchingDeviceId();
  }

  async processNewAsset() {
    if (!this.canProcessNewAsset()) return;

    this.loading.set(true);

    const proximitySamplePoint: FixedSamplePoint = {
      id: randomUuid(),
      name: this.store.name(),
      location: {
        coords: [this.store.lat()!, this.store.long()!]
      },
      sampleBasedLocation: this.store.sampleBasedLocation(),
      farmId: this.farmStore.farm()!.id,
      synchronizationStatus: SynchronizationStatus.creationPending
    }

    try {
      await this.dataSynchronisationService.applyAction(
        DataType.proximitySamplePoints,
        ActionType.createAsset,
        proximitySamplePoint,
        SynchronizationStatus.creationPending
      );

      await this.dataSynchronisationService.applyAction(
        DataType.deviceAssociations,
        ActionType.associateAssetWithDevice,
        {
          id: this.store.deviceToMatch()?.id! + proximitySamplePoint.id,
          version: 0,
          startDateTime: new Date().toISOString(),
          assetId: proximitySamplePoint.id,
          deviceId: this.store.deviceToMatch()?.id,
          synchronizationStatus: SynchronizationStatus.syncPending
        },
        SynchronizationStatus.syncPending
      );

      this.bootstrapService.reloadFixedAssetSamplePoints();

      this.internalNotificationsStore.addNotification("Successfully added BLE scanner.", InternalNotificationType.SUCCESS, false);

      if (this.destination === Destination.PROXIMITY_SAMPLE_POINTS) {
        this.router.navigate(['/main/proximity-sample-points']);
      }

      this.reset()
    } catch (error) {
      this.internalNotificationsStore.addNotification(
        "Unable to add BLE scanner.",
        InternalNotificationType.ERROR,
        true
      );
      this.loading.set(false);
    }
  }

  private processMatchingDeviceId() {
    const deviceId = this.store.deviceToMatch()?.id;
    if (!deviceId) {
      this.initializeForm();
      return;
    }

    const deviceAssociation = this.deviceAssociationStore.deviceIdToAssociation().get(deviceId);
    if (!deviceAssociation) {
      this.initializeForm();
      return;
    }

    this.existingDeviceAssociation.set(deviceAssociation);
    this.existingAsset.set(this.fixedSamplePointsStore.entityMap()[deviceAssociation.assetId]);
  }

  setName(event: Event) {
    if (!event.target) return;

    const string = (event.target as HTMLInputElement).value;
    this.store.setName(string === '' ? undefined : string);
  }

  setLat(event: Event) {
    if (!event.target) return;

    const string = (event.target as HTMLInputElement).value;
    this.store.setLat(parseOptionalFloat(string));
  }

  setLong(event: Event) {
    if (!event.target) return;

    const string = (event.target as HTMLInputElement).value;
    this.store.setLong(parseOptionalFloat(string));
  }

  private initializeForm() {
    const farm = this.farmStore.farm();
    if (!farm || !farm.location || !farm.location.coords || !farm.location.coords[0] || !farm.location.coords[1]) return;

    if (this.store.lat() !== undefined && this.store.long() !== undefined) return;

    this.store.setLat(farm.location.coords[0]);
    this.store.setLong(farm.location.coords[1]);
  }

  async deleteDeviceAssociation() {
    const association = this.existingDeviceAssociation();
    if (!association) return;

    const asset = this.existingAsset();
    if (asset) {
      await this.dataSynchronisationService.dissociateAsset(asset, DataType.proximitySamplePoints);
    } else {
      await this.dataSynchronisationService.dissociateDeviceAssociation(association);
    }

    this.internalNotificationsStore.addNotification("Successfully deleted asset.", InternalNotificationType.SUCCESS, false);

    this.bootstrapService.reloadFixedAssetSamplePoints();

    this.reset();
  }

  setOnboardingAction(onboardingAction: OnboardingAction) {
    this.store.setOnboardingAction(onboardingAction);
  }

  toggleSampleBasedLocation() {
    this.store.setSampleBasedLocation(!this.store.sampleBasedLocation());
  }

  reset() {
    this.loading.set(false);
    this.existingDeviceAssociation.set(undefined);
    this.existingAsset.set(undefined);
    this.store.reset();
  }

  async associate(assetId: string) {
    const deviceId = this.store.deviceToMatch()?.id;
    if (!deviceId) return;

    await this.dataSynchronisationService.applyAction(
      DataType.deviceAssociations,
      ActionType.associateAssetWithDevice,
      {
        id: deviceId + assetId,
        version: 0,
        startDateTime: new Date().toISOString(),
        assetId: assetId,
        deviceId: deviceId,
        synchronizationStatus: SynchronizationStatus.syncPending
      },
      SynchronizationStatus.syncPending
    );

      this.internalNotificationsStore.addNotification("Successfully added BLE scanner.", InternalNotificationType.SUCCESS, false);

      if (this.destination === Destination.PROXIMITY_SAMPLE_POINTS) {
        this.router.navigate(['/main/proximity-sample-points']);
      }

      this.bootstrapService.reloadFixedAssetSamplePoints();

      this.reset()
  }

  onDropAPin() {
    this.menuStore.disable();
    this.dropAPinNotification = this.internalNotificationsStore.addNotification(dropAPinMessage, InternalNotificationType.INFO, true);
    this.dataSubscriptionService.publish('dropAPin', true)
    this.dataSubscriptionService.onFirst<{
      lng: number,
      lat: number
    }>('currentPosition').pipe(takeUntil(this.$destroyDropAPin)).subscribe(data => {
      if (confirm('Are you sure you want to use this location for this BLE scanner?')) {
        this.store.setLat(data.lat);
        this.store.setLong(data.lng);
      }
      this.menuStore.enable();
      this.$destroyDropAPin.complete();
      this.$destroyDropAPin = new Subject<void>();
      this.dataSubscriptionService.publish('currentPosition');
      if (this.dropAPinNotification) this.internalNotificationsStore.deleteNotification(this.dropAPinNotification);
      this.dataSubscriptionService.publish('dropAPin', false);
      this.mainViewStore.showChildComponent();
    });
    this.mainViewStore.hideChildComponent();
  }
}
